import { useState, useEffect } from "react";
import { X, Plus, Trash2, Save, ChevronLeft, ChevronRight, Dumbbell, Settings, Search, Link2, Unlink } from "lucide-react";
import ExercisePickerModal from "./ExercisePickerModal";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";
const NIVEAUS = ["Beginner", "Gemiddeld", "Gevorderd", "Elite"];
const SS_KLEUREN = { A:"#C9A84C", B:"#60a5fa", C:"#a78bfa", D:"#34d399", E:"#f87171", F:"#fb923c" };

/** Volgende beschikbare superset letter */
function getNextLetter(oefeningen) {
  const used = new Set(oefeningen.map(o => o.superset_groep).filter(Boolean));
  for (const l of "ABCDEFGHIJKLMNOP") if (!used.has(l)) return l;
  return "A";
}

/** Flat lijst → visuele groepen [{type:'single'|'superset', ...}] */
function buildGroepen(oefeningen) {
  const groups = [];
  let i = 0;
  while (i < oefeningen.length) {
    const groep = oefeningen[i].superset_groep;
    if (groep) {
      const items = [];
      while (i < oefeningen.length && oefeningen[i].superset_groep === groep) {
        items.push({ oef: oefeningen[i], idx: i });
        i++;
      }
      groups.push({ type: "superset", letter: groep, items });
    } else {
      groups.push({ type: "single", oef: oefeningen[i], idx: i });
      i++;
    }
  }
  return groups;
}

function OefeningRij({ oef, idx, onChange, onDelete, token, onStartSuperset, inSuperset }) {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <div className="flex items-start gap-3 p-3.5 rounded-xl border bg-white/2 group"
      style={{ borderColor: inSuperset ? "transparent" : "rgba(255,255,255,0.08)" }}>
      {showPicker && (
        <ExercisePickerModal
          token={token}
          onClose={() => setShowPicker(false)}
          onSelect={ex => {
            onChange({ ...oef, naam: ex.naam, spiergroep: ex.spiergroep || oef.spiergroep });
            setShowPicker(false);
          }}
        />
      )}
      <div className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black text-black shrink-0 mt-0.5"
        style={{ background: GOLD }}>
        {idx + 1}
      </div>
      <div className="flex-1 space-y-2">
        {/* Naam + verander knop */}
        <div className="flex items-center gap-2">
          <input
            value={oef.naam || ""}
            onChange={e => onChange({ ...oef, naam: e.target.value })}
            placeholder="Oefening naam..."
            className="flex-1 bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-zinc-700 focus:outline-none focus:border-white/25"
          />
          <button onClick={() => setShowPicker(true)} data-testid={`open-exercise-picker-${idx}`}
            title="Verander oefening"
            className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-colors hover:bg-white/10 whitespace-nowrap"
            style={{ border: `1px solid ${GOLD}40`, color: GOLD }}>
            <Search size={11} /> Verander
          </button>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[
            { label:"Sets", key:"sets", type:"number", placeholder:"3" },
            { label:"Reps", key:"reps", placeholder:"10" },
            { label:"Rust",  key:"rust",  placeholder:"90s" },
            { label:"Tempo", key:"tempo", placeholder:"2-0-2" },
          ].map(f => (
            <div key={f.key}>
              <label className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider block mb-1">{f.label}</label>
              <input type={f.type || "text"} min="1"
                value={oef[f.key] ?? (f.type === "number" ? 3 : "")}
                onChange={e => onChange({ ...oef, [f.key]: f.type === "number" ? (parseInt(e.target.value)||3) : e.target.value })}
                placeholder={f.placeholder}
                className="w-full bg-transparent border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm text-center focus:outline-none focus:border-white/25"
              />
            </div>
          ))}
        </div>
        <input value={oef.notities || ""}
          onChange={e => onChange({ ...oef, notities: e.target.value })}
          placeholder="Uitvoeringstips, notities..."
          className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-1.5 text-zinc-500 text-xs placeholder:text-zinc-800 focus:outline-none focus:border-white/20"
        />
      </div>
      <div className="flex flex-col gap-1 mt-0.5">
        {!inSuperset && onStartSuperset && (
          <button onClick={onStartSuperset}
            data-testid={`start-superset-${idx}`}
            title="Superset starten"
            className="w-7 h-7 rounded-lg flex items-center justify-center text-zinc-600 hover:text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-colors"
          >
            <Link2 size={12} />
          </button>
        )}
        <button onClick={onDelete}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-zinc-700 hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

function SupersetBlok({ letter, items, token, onChangeOef, onDeleteOef, onAddToSuperset, onOntbinden }) {
  const kleur = SS_KLEUREN[letter] || GOLD;
  return (
    <div className="rounded-2xl border overflow-hidden"
      style={{ borderColor: `${kleur}35`, background: `${kleur}06` }}>
      {/* Superset header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b"
        style={{ borderColor: `${kleur}25`, background: `${kleur}10` }}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black"
            style={{ background: kleur, color: "#000" }}>
            {letter}
          </div>
          <span className="text-xs font-black uppercase tracking-widest" style={{ color: kleur }}>
            Superset {letter}
          </span>
          <span className="text-zinc-600 text-xs">·  {items.length} oefeningen</span>
        </div>
        <button onClick={onOntbinden} data-testid={`ontbinden-superset-${letter}`}
          title="Superset ontbinden"
          className="flex items-center gap-1.5 text-zinc-600 hover:text-zinc-300 text-xs transition-colors px-2 py-1 rounded-lg hover:bg-white/5">
          <Unlink size={11} /> Ontbinden
        </button>
      </div>

      {/* Oefeningen in de superset */}
      <div className="p-3 space-y-2">
        {items.map(({ oef, idx }, i) => (
          <OefeningRij
            key={oef.id || idx}
            oef={oef}
            idx={idx}
            token={token}
            inSuperset
            onChange={updated => onChangeOef(idx, updated)}
            onDelete={() => onDeleteOef(idx)}
          />
        ))}

        {/* + Oefening toevoegen aan superset */}
        <button onClick={onAddToSuperset}
          data-testid={`add-to-superset-${letter}`}
          className="w-full py-2 rounded-xl border border-dashed text-xs font-bold flex items-center justify-center gap-1.5 transition-colors"
          style={{ borderColor: `${kleur}40`, color: kleur }}>
          <Plus size={12} /> Oefening toevoegen aan Superset {letter}
        </button>
      </div>
    </div>
  );
}

export default function TemplateEditorModal({ templateId, token, onClose, onSaved }) {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actieveDag, setActieveDag] = useState(0);
  const [error, setError] = useState("");
  const [toonMeta, setToonMeta] = useState(false);

  useEffect(() => {
    if (!templateId) return;
    setLoading(true);
    fetch(`${API}/api/admin/schema-templates/${templateId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setTemplate(d); setLoading(false); })
      .catch(() => { setError("Template laden mislukt"); setLoading(false); });
  }, [templateId, token]);

  const updateOefening = (dagIdx, oefIdx, updated) => {
    const dagen = [...template.dagen];
    const oefeningen = [...(dagen[dagIdx].oefeningen || [])];
    oefeningen[oefIdx] = updated;
    dagen[dagIdx] = { ...dagen[dagIdx], oefeningen };
    setTemplate({ ...template, dagen });
  };

  const deleteOefening = (dagIdx, oefIdx) => {
    const dagen = [...template.dagen];
    let oefeningen = (dagen[dagIdx].oefeningen || []).filter((_, i) => i !== oefIdx);
    // Als er nog maar 1 over is in een superset-groep → ontbinden
    const groepTelling = {};
    oefeningen.forEach(o => { if (o.superset_groep) groepTelling[o.superset_groep] = (groepTelling[o.superset_groep]||0)+1; });
    oefeningen = oefeningen.map(o => ({
      ...o,
      superset_groep: o.superset_groep && groepTelling[o.superset_groep] < 2 ? null : o.superset_groep
    }));
    dagen[dagIdx] = { ...dagen[dagIdx], oefeningen };
    setTemplate({ ...template, dagen });
  };

  const addOefening = (dagIdx) => {
    const dagen = [...template.dagen];
    const oefeningen = [...(dagen[dagIdx].oefeningen || [])];
    oefeningen.push({
      id: Math.random().toString(36).slice(2, 10),
      naam: "", spiergroep: "", sets: 3, reps: "10",
      gewicht: "", rust: "90 sec", tempo: "", notities: "",
      superset_groep: null, volgorde: oefeningen.length,
    });
    dagen[dagIdx] = { ...dagen[dagIdx], oefeningen };
    setTemplate({ ...template, dagen });
  };

  /** Superset starten: huidige oefening + nieuw lege direct eronder koppelen */
  const startSuperset = (dagIdx, oefIdx) => {
    const dagen = [...template.dagen];
    const oefeningen = [...(dagen[dagIdx].oefeningen || [])];
    const letter = getNextLetter(oefeningen);
    oefeningen[oefIdx] = { ...oefeningen[oefIdx], superset_groep: letter };
    // Voeg direct een nieuwe lege oefening toe na de huidige
    const nieuweOef = {
      id: Math.random().toString(36).slice(2, 10),
      naam: "", spiergroep: "", sets: oefeningen[oefIdx].sets || 3,
      reps: "10", gewicht: "", rust: "90 sec", tempo: "",
      notities: "", superset_groep: letter, volgorde: oefIdx + 1,
    };
    oefeningen.splice(oefIdx + 1, 0, nieuweOef);
    dagen[dagIdx] = { ...dagen[dagIdx], oefeningen };
    setTemplate({ ...template, dagen });
  };

  /** Extra oefening toevoegen aan bestaande superset */
  const addToSuperset = (dagIdx, letter) => {
    const dagen = [...template.dagen];
    const oefeningen = [...(dagen[dagIdx].oefeningen || [])];
    // Vind positie van laatste oefening in deze groep
    let lastIdx = -1;
    oefeningen.forEach((o, i) => { if (o.superset_groep === letter) lastIdx = i; });
    const nieuweOef = {
      id: Math.random().toString(36).slice(2, 10),
      naam: "", spiergroep: "", sets: 3, reps: "10",
      gewicht: "", rust: "90 sec", tempo: "",
      notities: "", superset_groep: letter, volgorde: lastIdx + 1,
    };
    oefeningen.splice(lastIdx + 1, 0, nieuweOef);
    dagen[dagIdx] = { ...dagen[dagIdx], oefeningen };
    setTemplate({ ...template, dagen });
  };

  /** Superset ontbinden: verwijder superset_groep van alle oefeningen in de groep */
  const ontbindSuperset = (dagIdx, letter) => {
    const dagen = [...template.dagen];
    const oefeningen = (dagen[dagIdx].oefeningen || []).map(o =>
      o.superset_groep === letter ? { ...o, superset_groep: null } : o
    );
    dagen[dagIdx] = { ...dagen[dagIdx], oefeningen };
    setTemplate({ ...template, dagen });
  };

  const addDag = () => {
    const dagen = [...(template.dagen || [])];
    dagen.push({ dag_naam: `Dag ${dagen.length + 1}`, oefeningen: [] });
    setTemplate({ ...template, dagen, trainingsdagen: dagen.length });
    setActieveDag(dagen.length - 1);
  };

  const removeDag = (dagIdx) => {
    if (template.dagen.length <= 1) return;
    const dagen = template.dagen.filter((_, i) => i !== dagIdx);
    setTemplate({ ...template, dagen, trainingsdagen: dagen.length });
    setActieveDag(d => Math.min(d, dagen.length - 1));
  };

  const saveTemplate = async () => {
    if (!template.naam?.trim()) { setError("Naam is verplicht"); return; }
    setSaving(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/admin/schema-templates/${templateId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          naam: template.naam,
          beschrijving: template.beschrijving || "",
          niveau: template.niveau || "Beginner",
          trainingsdagen: template.dagen?.length || template.trainingsdagen || 3,
          weken: template.weken || 6,
          dagen: template.dagen || [],
        }),
      });
      if (!r.ok) throw new Error("Opslaan mislukt");
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const dagCount = template?.dagen?.length || 0;
  const huidigeDag = template?.dagen?.[actieveDag];

  return (
    <div
      className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        data-testid="template-editor-modal"
        className="w-full sm:w-[740px] max-h-[94vh] flex flex-col rounded-t-3xl sm:rounded-3xl border border-white/10"
        style={{ background: "#0d0d0d" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-white/8 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${GOLD}20` }}>
              <Dumbbell size={16} style={{ color: GOLD }} />
            </div>
            <div>
              <h2 className="text-white font-black text-base leading-tight">
                {loading ? "Laden..." : template?.naam || "Template bewerken"}
              </h2>
              <p className="text-zinc-600 text-xs">Schema template inhoud bewerken</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setToonMeta(v => !v)}
              data-testid="template-meta-toggle"
              className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${toonMeta ? "bg-[#C9A84C]/20 text-[#C9A84C]" : "bg-white/5 text-zinc-500 hover:text-white"}`}
              title="Template instellingen"
            >
              <Settings size={15} />
            </button>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
            >
              <X size={15} className="text-zinc-400" />
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex-1 flex items-center justify-center py-16">
            <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: GOLD, borderTopColor: "transparent" }} />
          </div>
        )}

        {!loading && template && (
          <>
            {/* Meta instellingen (inklapbaar) */}
            {toonMeta && (
              <div className="px-6 py-4 border-b border-white/6 space-y-3 shrink-0 bg-white/2">
                <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest">Template instellingen</p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider">Naam</span>
                    <input
                      value={template.naam || ""}
                      onChange={e => setTemplate({ ...template, naam: e.target.value })}
                      className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/25"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider">Niveau</span>
                    <select
                      value={template.niveau || "Beginner"}
                      onChange={e => setTemplate({ ...template, niveau: e.target.value })}
                      className="bg-[#111] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                    >
                      {NIVEAUS.map(n => <option key={n}>{n}</option>)}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider">Weken</span>
                    <input
                      type="number" min="1" max="52"
                      value={template.weken || 6}
                      onChange={e => setTemplate({ ...template, weken: parseInt(e.target.value) || 6 })}
                      className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white text-sm text-center focus:outline-none"
                    />
                  </label>
                  <label className="flex flex-col gap-1 col-span-2">
                    <span className="text-zinc-600 text-[10px] font-bold uppercase tracking-wider">Beschrijving</span>
                    <textarea
                      value={template.beschrijving || ""}
                      onChange={e => setTemplate({ ...template, beschrijving: e.target.value })}
                      rows={2}
                      className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none resize-none"
                    />
                  </label>
                </div>
              </div>
            )}

            {/* Dag-tabs */}
            <div className="flex items-center gap-1 px-6 py-3 overflow-x-auto shrink-0">
              {template.dagen.map((dag, i) => (
                <button
                  key={i}
                  onClick={() => setActieveDag(i)}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all shrink-0"
                  style={actieveDag === i
                    ? { background: GOLD, color: "#000" }
                    : { background: "rgba(255,255,255,0.05)", color: "#71717a" }
                  }
                >
                  {dag.dag_naam || `Dag ${i + 1}`}
                </button>
              ))}
              <button
                onClick={addDag}
                data-testid="template-add-dag-btn"
                className="px-2.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap shrink-0 transition-all"
                style={{ background: "rgba(201,168,76,0.08)", color: GOLD, border: `1px dashed ${GOLD}40` }}
              >
                <Plus size={12} className="inline" /> Dag
              </button>
            </div>

            {/* Dag inhoud */}
            <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-3">
              {huidigeDag && (
                <>
                  {/* Dag naam + verwijder */}
                  <div className="flex items-center gap-2">
                    <input
                      value={huidigeDag.dag_naam || ""}
                      onChange={e => {
                        const dagen = [...template.dagen];
                        dagen[actieveDag] = { ...dagen[actieveDag], dag_naam: e.target.value };
                        setTemplate({ ...template, dagen });
                      }}
                      placeholder="Naam van deze trainingsdag..."
                      className="flex-1 bg-transparent border border-white/8 rounded-xl px-4 py-2.5 text-white font-bold text-sm focus:outline-none focus:border-white/20"
                    />
                    {template.dagen.length > 1 && (
                      <button
                        onClick={() => removeDag(actieveDag)}
                        className="w-9 h-9 rounded-xl text-zinc-600 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>

                  {/* Oefeningen — gegroepeerd (supersets + singles) */}
                  <div className="space-y-2">
                    {buildGroepen(huidigeDag.oefeningen || []).map((groep, gi) =>
                      groep.type === "superset" ? (
                        <SupersetBlok
                          key={`ss-${groep.letter}`}
                          letter={groep.letter}
                          items={groep.items}
                          token={token}
                          onChangeOef={(oefIdx, updated) => updateOefening(actieveDag, oefIdx, updated)}
                          onDeleteOef={(oefIdx) => deleteOefening(actieveDag, oefIdx)}
                          onAddToSuperset={() => addToSuperset(actieveDag, groep.letter)}
                          onOntbinden={() => ontbindSuperset(actieveDag, groep.letter)}
                        />
                      ) : (
                        <OefeningRij
                          key={groep.oef.id || groep.idx}
                          oef={groep.oef}
                          idx={groep.idx}
                          token={token}
                          onStartSuperset={() => startSuperset(actieveDag, groep.idx)}
                          onChange={updated => updateOefening(actieveDag, groep.idx, updated)}
                          onDelete={() => deleteOefening(actieveDag, groep.idx)}
                        />
                      )
                    )}
                  </div>

                  <button
                    data-testid="template-add-oefening-btn"
                    onClick={() => addOefening(actieveDag)}
                    className="w-full py-2.5 rounded-xl border border-dashed border-white/15 text-zinc-600 hover:text-zinc-400 hover:border-white/25 text-sm flex items-center justify-center gap-2 transition-colors"
                  >
                    <Plus size={14} /> Oefening toevoegen
                  </button>
                </>
              )}
            </div>

            {/* Navigatie */}
            <div className="flex items-center justify-between px-6 pb-2 shrink-0">
              <button
                onClick={() => setActieveDag(d => Math.max(0, d - 1))}
                disabled={actieveDag === 0}
                className="flex items-center gap-1.5 text-zinc-600 hover:text-zinc-300 text-xs disabled:opacity-30 transition-colors"
              >
                <ChevronLeft size={14} /> Vorige dag
              </button>
              <span className="text-zinc-700 text-xs">
                Dag {actieveDag + 1} / {dagCount} · {(huidigeDag?.oefeningen || []).length} oefeningen
              </span>
              <button
                onClick={() => setActieveDag(d => Math.min(dagCount - 1, d + 1))}
                disabled={actieveDag === dagCount - 1}
                className="flex items-center gap-1.5 text-zinc-600 hover:text-zinc-300 text-xs disabled:opacity-30 transition-colors"
              >
                Volgende dag <ChevronRight size={14} />
              </button>
            </div>
          </>
        )}

        {error && (
          <p className="text-red-400 text-xs text-center px-6 pb-2">{error}</p>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/8 flex gap-3 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-white/10 text-zinc-400 text-sm font-bold hover:bg-white/5 transition-colors"
          >
            Annuleren
          </button>
          <button
            data-testid="template-editor-save-btn"
            onClick={saveTemplate}
            disabled={saving || loading}
            className="flex-1 py-3 rounded-xl font-black text-sm text-black flex items-center justify-center gap-2 disabled:opacity-40 transition-all active:scale-[0.98]"
            style={{ background: GOLD }}
          >
            <Save size={14} />
            {saving ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}
